import {
  compositionSkillIds,
  compositionStepCount,
  type PublicOutcomeSummary,
} from '../landing/outcomeShowcase';
import type { Outcome } from '../../api';
import { playbookDiagram } from '../composer/diagram';

export type MarketplaceCard = {
  id: string;
  slug?: string;
  name: string;
  tagline: string;
  description: string;
  category: string;
  priceCents: number;
  score: number;
  steps: number;
  downloads: number;
  author: string;
  authorInitials: string;
  tags: string[];
  diagram: string;
};

export const CATEGORY_ICONS: Record<string, string> = {
  'Code Quality': 'ri-search-eye-line',
  Engineering: 'ri-code-s-slash-line',
  Security: 'ri-shield-keyhole-line',
  Content: 'ri-quill-pen-line',
  Data: 'ri-database-2-line',
  Analytics: 'ri-bar-chart-2-line',
  Workflow: 'ri-flow-chart',
  Agent: 'ri-robot-line',
  SEO: 'ri-search-line',
  Research: 'ri-book-open-line',
  Productivity: 'ri-mail-send-line',
  'AI/ML': 'ri-brain-line',
  API: 'ri-plug-line',
  Design: 'ri-palette-line',
  Marketing: 'ri-megaphone-line',
  Media: 'ri-film-line',
  Operations: 'ri-settings-3-line',
  'Paid Ads': 'ri-advertisement-line',
  Social: 'ri-share-line',
  Strategy: 'ri-compass-3-line',
  General: 'ri-stack-line',
};

export type CategoryTheme = {
  icon: string;
  wash: string;
  chip: string;
  ink: string;
};

const CATEGORY_WASH: Record<string, Omit<CategoryTheme, 'icon'>> = {
  engineering: { wash: 'bg-sky-500/10', chip: 'bg-sky-500/10 text-sky-500 border-sky-500/25', ink: 'text-sky-500' },
  'code quality': { wash: 'bg-sky-500/10', chip: 'bg-sky-500/10 text-sky-500 border-sky-500/25', ink: 'text-sky-500' },
  security: { wash: 'bg-emerald-500/10', chip: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/25', ink: 'text-emerald-500' },
  research: { wash: 'bg-violet-500/10', chip: 'bg-violet-500/10 text-violet-500 border-violet-500/25', ink: 'text-violet-500' },
  productivity: { wash: 'bg-amber-500/10', chip: 'bg-amber-500/10 text-amber-500 border-amber-500/25', ink: 'text-amber-500' },
  content: { wash: 'bg-orange-500/10', chip: 'bg-orange-500/10 text-orange-500 border-orange-500/25', ink: 'text-orange-500' },
  data: { wash: 'bg-cyan-500/10', chip: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/25', ink: 'text-cyan-500' },
  analytics: { wash: 'bg-cyan-500/10', chip: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/25', ink: 'text-cyan-500' },
  workflow: { wash: 'bg-teal-500/10', chip: 'bg-teal-500/10 text-teal-500 border-teal-500/25', ink: 'text-teal-500' },
  agent: { wash: 'bg-fuchsia-500/10', chip: 'bg-fuchsia-500/10 text-fuchsia-500 border-fuchsia-500/25', ink: 'text-fuchsia-500' },
  seo: { wash: 'bg-lime-500/10', chip: 'bg-lime-500/10 text-lime-500 border-lime-500/25', ink: 'text-lime-500' },
  'ai/ml': { wash: 'bg-indigo-500/10', chip: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/25', ink: 'text-indigo-500' },
  api: { wash: 'bg-blue-500/10', chip: 'bg-blue-500/10 text-blue-500 border-blue-500/25', ink: 'text-blue-500' },
  design: { wash: 'bg-pink-500/10', chip: 'bg-pink-500/10 text-pink-500 border-pink-500/25', ink: 'text-pink-500' },
  marketing: { wash: 'bg-rose-500/10', chip: 'bg-rose-500/10 text-rose-500 border-rose-500/25', ink: 'text-rose-500' },
  media: { wash: 'bg-rose-500/10', chip: 'bg-rose-500/10 text-rose-500 border-rose-500/25', ink: 'text-rose-500' },
  operations: { wash: 'bg-slate-500/10', chip: 'bg-slate-500/10 text-slate-500 border-slate-500/25', ink: 'text-slate-500' },
  'paid ads': { wash: 'bg-yellow-500/10', chip: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/25', ink: 'text-yellow-500' },
  social: { wash: 'bg-blue-500/10', chip: 'bg-blue-500/10 text-blue-500 border-blue-500/25', ink: 'text-blue-500' },
  strategy: { wash: 'bg-indigo-500/10', chip: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/25', ink: 'text-indigo-500' },
  general: { wash: 'bg-surface2', chip: 'bg-bg text-texts border-border/60', ink: 'text-accent' },
};

const FALLBACK_THEME: Omit<CategoryTheme, 'icon'> = {
  wash: 'bg-surface2',
  chip: 'bg-bg text-texts border-border/60',
  ink: 'text-accent',
};

export const SORT_OPTIONS = [
  { label: 'Most popular', value: 'popular' },
  { label: 'Highest score', value: 'score' },
  { label: 'Lowest price', value: 'price-asc' },
  { label: 'Highest price', value: 'price-desc' },
];

export function formatPrice(cents: number): string {
  if (!cents) return 'Free';
  if (cents % 100 === 0) return `$${cents / 100}`;
  return `$${(cents / 100).toFixed(2)}`;
}

export function marketplacePath(card: Pick<MarketplaceCard, 'id' | 'slug'>): string {
  return `/marketplace/${card.slug || card.id}`;
}

export function categoryIcon(category: string): string {
  if (CATEGORY_ICONS[category]) return CATEGORY_ICONS[category];
  const lower = category.trim().toLowerCase();
  for (const [label, icon] of Object.entries(CATEGORY_ICONS)) {
    if (label.toLowerCase() === lower) return icon;
  }
  return 'ri-stack-line';
}

export function categoryTheme(category: string): CategoryTheme {
  const key = category.trim().toLowerCase();
  const wash = CATEGORY_WASH[key] ?? FALLBACK_THEME;
  return { icon: categoryIcon(category), ...wash };
}

function firstSentence(text: string): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (!trimmed) return '';
  const m = trimmed.match(/^[^.!?]+[.!?]/);
  return m ? m[0].trim() : trimmed;
}

function skillTag(id: string): string {
  return id.replace(/^skill-/, '').replace(/_/g, '-');
}

export function toMarketplaceCard(o: PublicOutcomeSummary): MarketplaceCard {
  const description = (o.plain_summary || o.description || o.playbook?.overview || '').trim();
  const author = (o.author || '').trim() || 'Sourcetape';
  const initials = (o.author_initials || '').trim() || author.slice(0, 2).toUpperCase();
  const skills = compositionSkillIds(o);
  const tags = skills.slice(0, 3).map(skillTag);
  if (o.discipline && !tags.some((t) => t.toLowerCase() === o.discipline!.toLowerCase())) {
    tags.unshift(o.discipline);
  }
  return {
    id: o.id,
    slug: o.slug,
    name: o.title,
    tagline: firstSentence(description) || o.title,
    description: description || 'No description recorded.',
    category: o.discipline || 'General',
    priceCents: o.price_cents ?? 0,
    score: o.score?.overall ?? o.quality_score ?? 0,
    steps: compositionStepCount(o),
    downloads: o.downloads ?? 0,
    author,
    authorInitials: initials.slice(0, 2),
    tags: tags.slice(0, 4),
    diagram: playbookDiagram(o),
  };
}

export function filterMarketplace(
  cards: MarketplaceCard[],
  opts: { search: string; category: string },
): MarketplaceCard[] {
  const q = opts.search.trim().toLowerCase();
  return cards.filter((p) => {
    const matchCat = opts.category === 'all' || p.category === opts.category;
    const matchSearch =
      !q ||
      [p.name, p.tagline, p.description, p.author, ...p.tags].join(' ').toLowerCase().includes(q);
    return matchCat && matchSearch;
  });
}

export function sortMarketplace(cards: MarketplaceCard[], sort: string): MarketplaceCard[] {
  const list = [...cards];
  switch (sort) {
    case 'score':
      return list.sort((a, b) => b.score - a.score);
    case 'price-asc':
      return list.sort((a, b) => a.priceCents - b.priceCents);
    case 'price-desc':
      return list.sort((a, b) => b.priceCents - a.priceCents);
    default:
      return list.sort((a, b) => b.downloads - a.downloads || b.score - a.score);
  }
}

export function marketplaceCategories(cards: MarketplaceCard[]): { label: string; value: string }[] {
  const set = new Set<string>();
  for (const c of cards) {
    if (c.category) set.add(c.category);
  }
  return [{ label: 'All', value: 'all' }, ...Array.from(set).sort().map((v) => ({ label: v, value: v }))];
}

export type AuthorPlaybook = {
  id: string;
  name: string;
  description: string;
  category: string;
  score: number;
  steps: number;
  published: boolean;
  priceCents: number;
  downloads: number;
  projectId?: string;
};

export function toAuthorPlaybook(o: Outcome, projectId?: string): AuthorPlaybook {
  const steps =
    o.composition?.pipeline_steps?.length ||
    o.pipeline_steps?.length ||
    o.composition?.nodes?.length ||
    o.nodes?.length ||
    0;
  return {
    id: o.id,
    name: o.title,
    description: o.description || o.playbook?.overview || '',
    category: o.discipline || 'General',
    score: o.score?.overall ?? o.quality_score ?? 0,
    steps,
    published: !!o.is_public,
    priceCents: o.price_cents ?? 0,
    downloads: o.downloads ?? 0,
    projectId,
  };
}

export function listingStats(playbooks: AuthorPlaybook[]): {
  published: number;
  drafts: number;
  earnings: number;
} {
  const published = playbooks.filter((p) => p.published);
  return {
    published: published.length,
    drafts: playbooks.length - published.length,
    earnings: published.reduce((sum, p) => sum + (p.priceCents * p.downloads) / 100, 0),
  };
}

export type Capability = { icon: string; title: string; description: string };

export function playbookCapabilities(o: PublicOutcomeSummary): Capability[] {
  const out: Capability[] = [];
  const steps = compositionStepCount(o);
  if (steps) {
    out.push({
      icon: 'ri-flow-chart',
      title: `${steps}-step pipeline`,
      description: compositionSkillIds(o).join(', ') || 'Ordered skills that run as one playbook.',
    });
  }
  for (const output of o.contract?.outputs ?? []) {
    out.push({
      icon: 'ri-file-list-3-line',
      title: output.name,
      description: output.type ? `Returns ${output.type}.` : 'A named output of this playbook.',
    });
  }
  for (const rule of o.playbook?.rules ?? []) {
    out.push({
      icon: 'ri-checkbox-circle-line',
      title: 'Playbook rule',
      description: rule,
    });
  }
  if (out.length === 0) {
    out.push({
      icon: 'ri-stack-line',
      title: 'Portable SKILL.md',
      description: 'Fork into Studio, then export Outcome.md plus one SKILL.md per canvas skill.',
    });
  }
  return out.slice(0, 4);
}

export type DerivedSuite = {
  name: string;
  status: 'passed' | 'failed';
  score: number;
  reason: string;
};

export function playbookTestResults(o: PublicOutcomeSummary): {
  passRate: number;
  passed: number;
  failed: number;
  total: number;
  runtime: string;
  summary: string;
  suites: DerivedSuite[];
} | null {
  const checks = o.score?.checks ?? [];
  const rig = o.test_rig;
  if (!checks.length && !rig) return null;
  const passed = checks.filter((c) => c.passed).length;
  const failed = checks.length - passed;
  const total = checks.length;
  const passRate = total ? Math.round((passed / total) * 100) : o.score?.overall ? Math.round((Number(o.score.overall) / 12) * 100) : 0;
  const runtime = rig?.latency_ms ? `${Math.round(rig.latency_ms / 1000)}s` : '';
  const summary =
    o.playbook?.overview ||
    o.plain_summary ||
    (total ? `${passed} of ${total} rubric checks passed.` : 'Certified score is published with this playbook.');
  return {
    passRate,
    passed,
    failed,
    total,
    runtime,
    summary,
    suites: checks.map((c) => ({
      name: c.criterion_id,
      status: c.passed ? 'passed' : 'failed',
      score: c.score,
      reason: c.reason,
    })),
  };
}

export function playbookFaqs(o: PublicOutcomeSummary): { question: string; answer: string }[] {
  const score = o.score?.overall ?? o.quality_score;
  const faqs = [
    {
      question: 'How is the score calculated?',
      answer: score
        ? `This playbook is scored ${Number(score).toFixed(1)}/12 from a live run against its rubric. The number is a harness score, not a user review.`
        : 'The SkillsBench score is 0-12 from a live run against the playbook rubric. This listing does not have a published score yet.',
    },
    {
      question: 'How do I use this playbook?',
      answer: 'Free playbooks fork into Studio as your own copy. Paid listings will check out through Stripe once billing for playbooks is on.',
    },
    {
      question: 'Can I export it?',
      answer: 'Yes. After you fork it, Studio export writes Outcome.md plus one SKILL.md per canvas skill.',
    },
  ];
  for (const mode of o.playbook?.failure_modes ?? []) {
    faqs.push({ question: 'What can go wrong?', answer: mode });
  }
  return faqs.slice(0, 5);
}
